import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Chip,
  List,
  ListItem,
  ListItemText,
  Paper,
  Popper,
  ClickAwayListener,
} from '@mui/material';

export function MemberSelector({
  label,
  selectedMembers = [],
  availableMembers = [],
  onChange,
}) {
  const [searchText, setSearchText] = useState('');
  const [anchorEl, setAnchorEl] = useState(null);
  const [open, setOpen] = useState(false);

  const filteredMembers = availableMembers
    .filter((member) => member.name.toLowerCase().includes(searchText.toLowerCase())
      && !selectedMembers.some((selected) => selected.id === member.id));

  const handleInputClick = (event) => {
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };

  const handleSelectMember = (member) => {
    const newSelected = [...selectedMembers, member];
    onChange(newSelected);
    setSearchText('');
  };

  const handleRemoveMember = (memberId) => {
    const newSelected = selectedMembers.filter((m) => m.id !== memberId);
    onChange(newSelected);
  };

  const handleClose = () => {
    setOpen(false);
    setAnchorEl(null);
    setSearchText('');
  };

  return (
    <Box>
      <Box sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        mb: 1,
      }}
      >
        <Box sx={{
          fontSize: '14px',
          fontWeight: '500',
          color: '#333',
        }}
        >
          {label}
        </Box>
      </Box>

      <ClickAwayListener onClickAway={handleClose}>
        <Box sx={{ position: 'relative' }}>
          <Box
            onClick={handleInputClick}
            sx={{
              border: '1px solid #e0e0e0',
              borderRadius: '4px',
              backgroundColor: '#f8f9fa',
              minHeight: '56px',
              maxHeight: '120px',
              padding: '8px 12px',
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'flex-start',
              gap: 0.5,
              overflow: 'auto',
              cursor: 'text',
              '&:hover': {
                borderColor: '#999',
              },
              '&:focus-within': {
                borderColor: '#1976d2',
                borderWidth: '2px',
              },
            }}
          >
            {/* Selected member chips */}
            {selectedMembers.map((member) => (
              <Chip
                key={member.id}
                label={member.name}
                onDelete={() => handleRemoveMember(member.id)}
                size="small"
                sx={{
                  height: '28px',
                  fontSize: '13px',
                  maxWidth: 'none',
                  '& .MuiChip-label': {
                    whiteSpace: 'nowrap',
                  },
                  '& .MuiChip-deleteIcon': {
                    fontSize: '18px',
                  },
                }}
              />
            ))}

            {/* Search input */}
            <TextField
              variant="standard"
              placeholder={selectedMembers.length === 0 ? `Search and select ${label.toLowerCase()}...` : ''}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              sx={{
                minWidth: '150px',
                flexGrow: 1,
                '& .MuiInput-underline:before': {
                  display: 'none',
                },
                '& .MuiInput-underline:after': {
                  display: 'none',
                },
                '& .MuiInputBase-input': {
                  fontSize: '14px',
                  padding: '4px 0',
                },
              }}
            />
          </Box>

          <Popper
            open={open}
            anchorEl={anchorEl}
            placement="bottom-start"
            style={{ width: anchorEl?.clientWidth, zIndex: 1300 }}
          >
            <Paper elevation={3} sx={{ maxHeight: 200, overflow: 'auto' }}>
              <List dense>
                {filteredMembers.length > 0 ? (
                  filteredMembers.map((member) => (
                    <ListItem
                      key={member.id}
                      button
                      onClick={() => handleSelectMember(member)}
                      sx={{ fontSize: '14px' }}
                    >
                      <ListItemText
                        primary={member.name}
                        primaryTypographyProps={{ fontSize: '14px' }}
                      />
                    </ListItem>
                  ))
                ) : (
                  <ListItem>
                    <ListItemText
                      primary="No members found"
                      primaryTypographyProps={{ fontSize: '14px', color: '#666' }}
                    />
                  </ListItem>
                )}
              </List>
            </Paper>
          </Popper>
        </Box>
      </ClickAwayListener>
    </Box>
  );
}

MemberSelector.propTypes = {
  label: PropTypes.string.isRequired,
  selectedMembers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string,
    }),
  ),
  availableMembers: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      email: PropTypes.string,
    }),
  ),
  onChange: PropTypes.func.isRequired,
};

MemberSelector.defaultProps = {
  selectedMembers: [],
  availableMembers: [],
};

export default MemberSelector;
